import { autoAnimatePlugin } from '@formkit/auto-animate/vue'
import { createInertiaApp, Link } from '@inertiajs/vue3'
import * as Sentry from '@sentry/vue'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'

import SidebarLayoutVue from './Layouts/SidebarLayout.vue'
import '../css/app.css'
import 'vue-sonner/style.css'
import { isExpectedElectricCancellation } from './utils/electric'
import { queryClient } from './utils/queryClient'
import { ignoreSuccessfulMapAssetSpans } from './utils/sentry'
import { startStartupMeasure } from './lib/startupPerformance'

import type { App as VueApp, DefineComponent } from 'vue'

const appName = 'FES Meldeportal'

function getEnvironment(): string {
  if (typeof document === 'undefined') {
    return 'local'
  }

  return document.querySelector<HTMLMetaElement>('meta[name="environment"]')?.content ?? 'local'
}

function getEnvironmentLabel(): string {
  const environment = getEnvironment()

  return environment === 'production' ? '' : ` [${environment}] `
}

function registerServiceWorker(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return
  }

  void navigator.serviceWorker.register('/sw.js', { scope: '/' })
}

function initSentry(app: VueApp, environment: string): void {
  Sentry.init({
    app,
    environment,
    dsn: 'https://8340ce6d3364b747b1e98731dee480b1@sentry.clickbar.app/54',
    integrations: [
      Sentry.browserTracingIntegration(),
      // Override the default browserApiErrors integration, which wraps every setTimeout/
      // setInterval/requestAnimationFrame callback for error context. TanStack DB batching and
      // Vue's scheduler fire a large cascade of those on each issue selection, and the per-call
      // wrapper overhead was the single biggest contributor to click-time dropped frames.
      Sentry.browserApiErrorsIntegration({
        setTimeout: false,
        setInterval: false,
        requestAnimationFrame: false,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        // Issue photos are served from /storage and can show faces, license plates and
        // house fronts — the same data AnonymizeIssueImage blurs server-side. Blocking
        // them by source covers every component that renders one, including future ones.
        block: ['img[src*="/storage/"]'],
        // Opt-in marker for personal data in text: guest reporter name/email and the
        // addresses of bulky waste appointments, which belong to a named household.
        mask: ['[data-pii]', '.pii'],
      }),
      Sentry.replayCanvasIntegration(),
      Sentry.feedbackIntegration({
        colorScheme: 'system',
        autoInject: false,
        showBranding: false,
        formTitle: 'Bug melden',
        submitButtonLabel: 'Absenden',
        cancelButtonLabel: 'Abbrechen',
        nameLabel: 'Name',
        namePlaceholder: 'Dein Name',
        emailLabel: 'E-Mail',
        emailPlaceholder: 'deine@email.de',
        messageLabel: 'Beschreibung',
        messagePlaceholder: 'Was ist passiert? Was hast du erwartet?',
        successMessageText: 'Danke für dein Feedback!',
        isRequiredLabel: '(Pflichtfeld)',
        addScreenshotButtonLabel: 'Screenshot hinzufügen',
        removeScreenshotButtonLabel: 'Screenshot entfernen',
      }),
    ],
    beforeSend(event, hint) {
      return isExpectedElectricCancellation(hint.originalException) ? null : event
    },
    ignoreSpans: ignoreSuccessfulMapAssetSpans,
    tracesSampleRate: 1,
    replaysSessionSampleRate: 0.5,
    replaysOnErrorSampleRate: 1,
  })
}

// Keep module evaluation synchronous: lazy page chunks can import helpers from the entry chunk.
async function mountApp(): Promise<void> {
  await createInertiaApp({
    title: (title: string) => `${getEnvironmentLabel()}${title} - ${appName}`,
    progress: {
      color: '#4B5563',
    },
    resolve: async (name: string) => {
      const finish = startStartupMeasure('page:resolve')
      const page = await resolvePageComponent(
        `./Pages/${name}.vue`,
        import.meta.glob<DefineComponent>('./Pages/**/*.vue'),
      )
      finish()

      if (!name.startsWith('Storybook/')) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        page.default.layout ??= SidebarLayoutVue
      }

      return page
    },
    withApp(app, { ssr }) {
      app.use(autoAnimatePlugin)
      app.component('inertia-link', Link)
      app.provide('queryClient', queryClient)

      if (ssr) {
        return
      }

      const environment = getEnvironment()
      registerServiceWorker()
      initSentry(app, environment)
    },
  })
  requestAnimationFrame(() => {
    performance.measure('meldeportal:app:first-frame', { start: 0, end: performance.now() })
  })
}

// oxlint-disable-next-line unicorn/prefer-top-level-await -- Lazy page chunks import entry exports; awaiting the entry can deadlock that cycle.
void mountApp()
